import { Navigate, useLocation } from "react-router-dom";
import { useMe } from "@/hooks/useAuth";
import { useTenantLayout } from "@/hooks/useTenantLayout";
import { AdminLayoutA } from "./AdminLayoutA";
import { AdminLayoutB } from "./AdminLayoutB";
import { AdminLayoutC } from "./AdminLayoutC";
import { Skeleton } from "@/components/ui/Skeleton";

/**
 * Picks the right admin layout for the signed-in tenant. Until the Tenant
 * Settings module ships, the layout is derived deterministically from
 * tenantId so different brokers automatically get visually distinct shells.
 */
export function AdminShell() {
  const { data: me, isPending, isError } = useMe();
  const location = useLocation();
  const { preset } = useTenantLayout(me ?? null);

  if (isPending) {
    return (
      <div className="min-h-screen bg-bg flex">
        <div className="w-64 border-r border-border p-4 space-y-2">
          <Skeleton className="h-9 w-32" />
          {Array.from({ length: 12 }).map((_, i) => (
            <Skeleton key={i} className="h-9 w-full" />
          ))}
        </div>
        <div className="flex-1 p-8 space-y-3">
          <Skeleton className="h-16 w-full" />
          <div className="grid grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-28 w-full" />
            ))}
          </div>
          <Skeleton className="h-72 w-full" />
        </div>
      </div>
    );
  }

  if (isError || !me) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  // Admins always in. A MANAGER granted an add-on admin group (fullAccess or any
  // permission) can also open the back office; a plain manager goes to their RM
  // portal; anyone else is bounced out.
  const managerHasAdminAccess = me.role === "manager" && (me.fullAccess || (me.permissions?.length ?? 0) > 0);
  if (me.role !== "admin" && !managerHasAdminAccess) {
    return <Navigate to={me.role === "manager" ? "/manager/dashboard" : "/login"} replace />;
  }

  switch (preset) {
    case "icon-rail":
      return <AdminLayoutB />;
    case "glass-floating":
      return <AdminLayoutC />;
    case "left-pinned":
    default:
      return <AdminLayoutA />;
  }
}
